import { useTranslation } from "react-i18next";
import { Button } from "../Button/Button";
import { Select } from "../Select/Select";
import styles from "./Pagination.module.css";

interface PaginationProps {
	page: number;
	pageSize: number;
	total: number;
	onPageChange: (page: number) => void;
	onPageSizeChange: (pageSize: number) => void;
}

export function Pagination({
	page,
	pageSize,
	total,
	onPageChange,
	onPageSizeChange,
}: PaginationProps) {
	const { t } = useTranslation();
	const totalPages = Math.ceil(total / pageSize) || 1;
	const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
	const end = Math.min(page * pageSize, total);

	function getVisiblePages() {
		const pages: (number | string)[] = [];
		const maxVisible = 5;
		if (totalPages <= maxVisible + 2) {
			for (let i = 1; i <= totalPages; i++) pages.push(i);
			return pages;
		}
		pages.push(1);
		let startPage = Math.max(2, page - 1);
		let endPage = Math.min(totalPages - 1, page + 1);
		if (page <= 3) {
			startPage = 2;
			endPage = Math.min(totalPages - 1, 4);
		}
		if (page >= totalPages - 2) {
			startPage = Math.max(2, totalPages - 3);
			endPage = totalPages - 1;
		}
		if (startPage > 2) pages.push("...");
		for (let i = startPage; i <= endPage; i++) pages.push(i);
		if (endPage < totalPages - 1) pages.push("...");
		pages.push(totalPages);
		return pages;
	}

	const visiblePages = getVisiblePages();

	return (
		<div className={styles.pagination}>
			<span className={styles.info}>{t("showingResults", { start, end, total })}</span>
			<div className={styles.controls}>
				<Button
					variant="ghost"
					size="sm"
					disabled={page <= 1}
					onClick={() => onPageChange(page - 1)}
				>
					{t("previous")}
				</Button>
				<div className={styles.pages}>
					{visiblePages.map((p, idx) =>
						typeof p === "string" ? (
							<span key={`dot-${idx}`} className={styles.ellipsis}>
								...
							</span>
						) : (
							<button
								key={p}
								type="button"
								className={[styles.pageBtn, p === page && styles.pageBtnActive]
									.filter(Boolean)
									.join(" ")}
								onClick={() => onPageChange(p)}
							>
								{p}
							</button>
						),
					)}
				</div>
				<Button
					variant="ghost"
					size="sm"
					disabled={page >= totalPages}
					onClick={() => onPageChange(page + 1)}
				>
					{t("next")}
				</Button>
				<Select
					options={[
						{ value: "10", label: "10" },
						{ value: "20", label: "20" },
						{ value: "50", label: "50" },
					]}
					value={String(pageSize)}
					onChange={(e) => {
						onPageSizeChange(Number(e.target.value));
					}}
					className={styles.pageSize}
				/>
			</div>
		</div>
	);
}
